import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck } from "lucide-react";

const Field = ({ label, value, mono = true }) => (
    <div className="py-3 border-b hairline last:border-b-0 grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-4">
            <span className="mono-label">{label}</span>
        </div>
        <div className="col-span-12 sm:col-span-8">
            <span
                className={mono ? "mono-data" : ""}
                style={{
                    fontSize: mono ? 12.5 : 14.5,
                    fontFamily: mono
                        ? "JetBrains Mono, monospace"
                        : "IBM Plex Sans, sans-serif",
                    color: "var(--ink)",
                }}
            >
                {value || "—"}
            </span>
        </div>
    </div>
);

export const ReceiptPanel = ({ response }) => {
    if (!response) return null;

    if (!response.ok || !response.receipt) {
        return (
            <section
                className="panel"
                data-testid="receipt-panel-unconfigured"
            >
                <div className="px-6 sm:px-8 py-5 border-b hairline">
                    <p className="mono-label">Step 06 · KATLAS receipt</p>
                    <h2
                        className="font-serif mt-1"
                        style={{ fontSize: 28, letterSpacing: "-0.01em" }}
                    >
                        No signed receipt produced
                    </h2>
                </div>
                <div className="px-6 sm:px-8 py-6">
                    <p style={{ color: "var(--ink-soft)" }}>
                        KATLAS is currently{" "}
                        <strong className="font-mono">
                            {response.katlas?.status}
                        </strong>
                        . Per the contract, no simulated receipt is produced. The
                        action is recorded as an honest unconfigured response.
                    </p>
                    <pre
                        className="mt-4 panel-alt px-4 py-3 mono-data overflow-x-auto"
                        style={{ fontSize: 12 }}
                    >
                        {JSON.stringify(response.katlas, null, 2)}
                    </pre>
                </div>
            </section>
        );
    }

    const r = response.receipt;

    return (
        <section
            className="bg-white"
            style={{
                border: "4px double var(--ink)",
            }}
            data-testid="receipt-panel"
        >
            <div className="px-6 sm:px-10 py-6 border-b hairline flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <ShieldCheck size={18} strokeWidth={1.5} />
                    <div>
                        <p className="mono-label">KATLAS · signed attestation</p>
                        <h2
                            className="font-serif mt-1"
                            style={{ fontSize: 26, letterSpacing: "-0.01em" }}
                        >
                            Cardiovascular review route —{" "}
                            <span style={{ color: "var(--forest)" }}>
                                {r.action_outcome}
                            </span>
                        </h2>
                    </div>
                </div>
                <span
                    className="mono-label border border-[var(--forest)] px-2 py-1"
                    style={{ color: "var(--forest)" }}
                >
                    {r.verification || "—"}
                </span>
            </div>

            <div className="px-6 sm:px-10 py-6">
                <Field label="Receipt hash" value={r.receipt_hash} />
                <Field
                    label="Attestation ref"
                    value={r.attestation_reference}
                />
                <Field label="Signer issuer" value={r.signer?.issuer} />
                <Field label="Signer key id" value={r.signer?.key_id} />
                <Field
                    label="Signer fingerprint"
                    value={r.signer_fingerprint}
                />
                <Field
                    label="Node timestamp"
                    value={r.node_timestamp_utc}
                />
                <Field
                    label="Local timestamp"
                    value={r.local_timestamp_utc}
                />
                <Field
                    label="Acting role"
                    value={r.acting_role?.label}
                    mono={false}
                />
                <Field label="Authority gate" value={r.authority_gate} />
                <Field
                    label="Shared evidence refs"
                    value={(r.shared_evidence_refs || [])
                        .map((e) => e.ref)
                        .join("  ·  ")}
                />
                <Field
                    label="Withheld evidence count"
                    value={String(r.withheld_evidence_count)}
                />
                <Field
                    label="Event"
                    value={`${r.event_id} · ${r.policy_id}`}
                />
            </div>

            <div className="px-6 sm:px-10 py-5 border-t hairline panel-alt">
                <p
                    className="font-serif"
                    style={{
                        fontSize: 15,
                        color: "var(--ink)",
                        lineHeight: 1.55,
                    }}
                >
                    {r.data_boundary_statement}
                </p>
                <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="mono-label">
                        C — {r.car_summary?.custody} A — {r.car_summary?.authority}{" "}
                        R — {r.car_summary?.receipts}
                    </div>
                    <Link
                        to={`/r/${encodeURIComponent(r.receipt_hash)}`}
                        className="inline-flex items-center gap-2 mono-label"
                        style={{ color: "var(--ink)" }}
                        data-testid="public-receipt-link"
                    >
                        Public-safe receipt
                        <ExternalLink size={12} strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default ReceiptPanel;
